// Stages requested settings within the V3 approval and displays confirmed results in place.
import { type ReactNode, useId, useState } from 'react';

import { SettingsRow } from '@apps/web/components/settings/settings_primitives';
import { Input } from '@apps/web/components/ui/input';
import { Switch } from '@apps/web/components/ui/switch';
import { type SettingsApprovalEditor } from '@apps/web/features/phoebe_chat_v3/model/settings_approval';
import { type WriteResultPresentation } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { ActionResultCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/action_result_card';
import { type ApprovalDecision } from '@apps/web/features/phoebe_chat_v3/ui/primitives/generic_approval_panel';
import { SettingsReceiptCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/settings_receipt_card';

export function SettingsApprovalPanel({
    editor,
    decision,
    outcome = 'pending',
    receipt,
    disabled = false,
    onSave,
    onCancel,
}: Readonly<{
    editor: SettingsApprovalEditor;
    decision: ApprovalDecision;
    outcome?: 'pending' | 'succeeded' | 'failed';
    receipt?: WriteResultPresentation;
    disabled?: boolean;
    onSave: (fields: Record<string, boolean | number | null>) => void;
    onCancel: () => void;
}>): ReactNode {
    const id = useId();
    const [drafts, setDrafts] = useState<Record<string, string | boolean>>(() =>
        Object.fromEntries(
            editor.fields.map((field) => [
                field.key,
                field.kind === 'toggle'
                    ? field.proposed
                    : field.proposed === null
                      ? ''
                      : String(field.proposed),
            ]),
        ),
    );
    const pending = decision === 'pending';
    const values: Record<string, boolean | number | null> = {};
    let invalid = false;
    const errors = new Map<string, string>();
    for (const field of editor.fields) {
        const draft = drafts[field.key];
        if (field.kind === 'toggle') {
            values[field.key] = draft === true;
            continue;
        }
        const value = draft === '' ? null : Number(draft);
        if (
            value === null
                ? !field.nullable
                : !Number.isFinite(value) ||
                  (field.step === 1 && !Number.isInteger(value)) ||
                  (field.min !== undefined && value < field.min) ||
                  (field.max !== undefined && value > field.max)
        ) {
            invalid = true;
            errors.set(
                field.key,
                field.min !== undefined && field.max !== undefined
                    ? `Enter a number between ${field.min} and ${field.max}.`
                    : field.min !== undefined
                      ? `Enter a number of at least ${field.min}.`
                      : 'Enter a valid number.',
            );
        }
        values[field.key] = value;
    }
    const changed = editor.fields.some(
        (field) => values[field.key] !== field.current,
    );
    const fields = (
        <div className="divide-y">
            {editor.fields.map((field) => {
                const fieldId = `${id}-${field.key}`;
                const applied = receipt?.settingsChanges?.[field.key];
                const value = pending
                    ? values[field.key]
                    : outcome === 'succeeded'
                      ? applied === undefined
                          ? field.current
                          : applied.to
                      : field.current;
                const error = pending ? errors.get(field.key) : undefined;
                return (
                    <SettingsRow
                        key={field.key}
                        title={field.label}
                        htmlFor={fieldId}
                        className="[&_label]:text-v3-chat px-0 py-2 last:pb-0 [&_label]:leading-(--text-v3-chat--line-height)"
                        description={
                            pending &&
                            field.kind === 'number' &&
                            field.nullable ? (
                                <span className="text-v3-chat">
                                    Leave blank for no limit
                                </span>
                            ) : undefined
                        }
                        control={
                            !pending && value === null ? (
                                <span className="text-v3-chat">Not set</span>
                            ) : field.kind === 'toggle' ? (
                                <Switch
                                    id={fieldId}
                                    className="disabled:opacity-100"
                                    checked={value === true}
                                    disabled={disabled || !pending}
                                    onCheckedChange={(checked) =>
                                        setDrafts((current) => ({
                                            ...current,
                                            [field.key]: checked,
                                        }))
                                    }
                                />
                            ) : (
                                <div className="w-20">
                                    <Input
                                        id={fieldId}
                                        type="number"
                                        className="text-v3-chat md:text-v3-chat h-9 text-right disabled:opacity-100"
                                        min={field.min}
                                        max={field.max}
                                        step={field.step ?? 'any'}
                                        placeholder={
                                            field.nullable
                                                ? 'No limit'
                                                : undefined
                                        }
                                        value={
                                            pending
                                                ? String(
                                                      drafts[field.key] ?? '',
                                                  )
                                                : value === null
                                                  ? ''
                                                  : String(value)
                                        }
                                        disabled={disabled || !pending}
                                        aria-invalid={error !== undefined}
                                        aria-describedby={
                                            error === undefined
                                                ? undefined
                                                : `${fieldId}-error`
                                        }
                                        onChange={(event) =>
                                            setDrafts((current) => ({
                                                ...current,
                                                [field.key]: event.target.value,
                                            }))
                                        }
                                    />
                                    {error !== undefined && (
                                        <p
                                            id={`${fieldId}-error`}
                                            className="text-destructive text-v3-chat-meta mt-1"
                                        >
                                            {error}
                                        </p>
                                    )}
                                </div>
                            )
                        }
                    />
                );
            })}
        </div>
    );
    if (!pending) {
        const status =
            decision === 'denied'
                ? 'denied'
                : outcome === 'pending'
                  ? 'running'
                  : outcome;
        if (status === 'succeeded' && receipt?.settingsChanges !== undefined) {
            return <SettingsReceiptCard receipt={receipt} />;
        }
        return (
            <ActionResultCard
                status={status}
                title={
                    status === 'denied'
                        ? editor.title
                        : status === 'running'
                          ? 'Saving settings…'
                          : status === 'failed'
                            ? 'Couldn’t save settings'
                            : (receipt?.title ?? 'Settings updated')
                }
                summary={status === 'failed' ? undefined : receipt?.summary}
                notices={status === 'succeeded' ? receipt?.notices : undefined}
            >
                {fields}
            </ActionResultCard>
        );
    }
    return (
        <form
            className="bg-card w-full min-w-0 rounded-[12px] border px-3 py-2.5 [overflow-wrap:anywhere]"
            onSubmit={(event) => {
                event.preventDefault();
                if (!invalid && changed && !disabled) {
                    onSave(values);
                }
            }}
        >
            <h3 className="text-v3-chat m-0 font-semibold">{editor.title}</h3>
            {fields}
            <div className="mt-2 flex gap-2">
                <button
                    type="submit"
                    disabled={disabled || invalid || !changed}
                    className="bg-foreground text-background focus-visible:ring-ring text-v3-chat min-h-8 rounded-md px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                >
                    Save
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onCancel}
                    className="text-muted-foreground focus-visible:ring-ring text-v3-chat min-h-8 rounded-md border px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}
